import { useOrganization } from '../hooks/useOrganization'
import { useAuth } from '../hooks/useAuth'
import { formatRoleLabel } from '../constants/roles'
import { useLocation, useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import styles from './OrganizationSwitcher.module.css'
import { organizationSwitchPath } from '../config/studioRoutes'

function OrganizationSwitcher({ switchable, id = 'organization-switcher' }) {
  const { currentOrg, organizations, switchOrganization, loading } = useOrganization()
  const { commandCenterAccess } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const handleSwitch = async (e) => {
    const slug = e.target.value
    if (!slug || slug === currentOrg?.slug) return

    // Checked against the refreshed organization: its grants may have changed.
    const nextOrganization = await switchOrganization(slug)
    if (!nextOrganization) return
    queryClient.clear()
    navigate(organizationSwitchPath(location.pathname, nextOrganization, { organizations, commandCenterAccess }))
  }

  // Show loading state while data is being fetched
  if (loading) {
    return (
      <div className={styles.orgDisplay}>
        <span className={styles.orgLabel}>Organization</span>
        <div className={styles.valueRow}>
          <span className={styles.orgName}>Loading...</span>
        </div>
      </div>
    )
  }
  
  // Handle case where there's no current org (shouldn't happen if authenticated)
  if (!currentOrg) {
    return (
      <div className={styles.orgDisplay}>
        <span className={styles.orgLabel}>Organization</span>
        <div className={styles.valueRow}>
          <span className={styles.orgName}>No organization</span>
        </div>
      </div>
    )
  }
  
  const canSwitch = switchable ?? organizations.length > 1

  if (!canSwitch || organizations.length <= 1) {
    return (
      <div className={styles.orgDisplay}>
        <span className={styles.orgLabel}>Organization</span>
        <div className={styles.valueRow}>
          <span className={styles.orgName}>{currentOrg.name || 'Unknown'}</span>
          <span className={styles.orgRole}>{formatRoleLabel(currentOrg.role)}</span>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.switcher}>
      <label className={styles.label} htmlFor={id}>Organization</label>
      <div className={styles.selectRow}>
        <select
          id={id}
          /* Command Center's queue has its own visible "Organization" filter;
             the shell switcher needs a distinct accessible name so the two
             are never ambiguous to assistive tech. */
          aria-label="Switch organization"
          value={currentOrg?.slug || ''}
          onChange={handleSwitch}
          className={styles.select}
          disabled={loading}
        >
          {!currentOrg?.slug && <option value="">Select organization</option>}
          {organizations.map(org => (
            <option key={org.id} value={org.slug}>
              {org.name || org.slug}
            </option>
          ))}
        </select>
        <span className={styles.orgRole}>{formatRoleLabel(currentOrg.role)}</span>
      </div>
    </div>
  )
}

export default OrganizationSwitcher
